import type { DiscoveredTool, McpAllowlistEntry } from '@sbc/domain-models/agents';
import { Link } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreateMcpAllowlistEntry,
  useDeleteAllMcpAllowlistEntries,
  useDeleteMcpAllowlistEntry,
  useUpdateMcpAllowlistEntry,
} from '@/lib/api/mcp-allowlist';
import { useDiscoverMcpTools, useMcpConnection } from '@/lib/api/mcp-connections';

function AllowlistRow({ entry }: { entry: McpAllowlistEntry }) {
  const updateEntry = useUpdateMcpAllowlistEntry(entry.id);
  const deleteEntry = useDeleteMcpAllowlistEntry(entry.id);
  const busy = updateEntry.isPending || deleteEntry.isPending;

  return (
    <TableRow>
      <TableCell className="font-medium">{entry.toolName}</TableCell>
      <TableCell>
        <Badge variant="outline">{entry.toolClass}</Badge>
      </TableCell>
      <TableCell>
        {entry.enabled ? (
          <Badge variant="secondary">Enabled</Badge>
        ) : (
          <Badge variant="outline">Disabled</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() =>
              updateEntry.mutate({ toolClass: entry.toolClass, enabled: !entry.enabled })
            }
          >
            {entry.enabled ? 'Disable' : 'Enable'}
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => deleteEntry.mutate()}>
            Delete
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function DiscoveredToolRow({
  connectionId,
  tool,
  isAllowlisted,
}: {
  connectionId: string;
  tool: DiscoveredTool;
  isAllowlisted: boolean;
}) {
  const createEntry = useCreateMcpAllowlistEntry();
  const toolClass = tool.readOnlyHint ? 'read' : 'write';

  return (
    <TableRow>
      <TableCell className="truncate font-medium" title={tool.name}>
        {tool.name}
      </TableCell>
      <TableCell
        className="truncate text-sm text-[var(--color-muted-foreground)]"
        title={tool.description}
      >
        {tool.description}
      </TableCell>
      <TableCell>
        <Badge variant="outline">{toolClass}</Badge>
      </TableCell>
      <TableCell className="text-right">
        {isAllowlisted ? (
          <Badge variant="secondary">Added</Badge>
        ) : (
          <Button
            size="sm"
            disabled={createEntry.isPending}
            onClick={() =>
              createEntry.mutate({ connectionId, toolName: tool.name, toolClass, enabled: true })
            }
          >
            {createEntry.isPending ? 'Adding…' : 'Add'}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

export function McpConnectionDetailPage({ connectionId }: { connectionId: string }) {
  const connectionQuery = useMcpConnection(connectionId);
  const discoverTools = useDiscoverMcpTools(connectionId);
  const createEntry = useCreateMcpAllowlistEntry();
  const deleteAll = useDeleteAllMcpAllowlistEntries(connectionId);

  if (connectionQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (connectionQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load connection: {connectionQuery.error.message}
      </p>
    );
  }

  const connection = connectionQuery.data;
  const allowlistedNames = new Set(connection.allowlist.map((entry) => entry.toolName));

  const discovered = discoverTools.data?.ok === true ? discoverTools.data.tools : [];
  const notYetAdded = (cls: 'read' | 'write') =>
    discovered.filter(
      (tool) => (tool.readOnlyHint ? 'read' : 'write') === cls && !allowlistedNames.has(tool.name),
    );
  const pendingReads = notYetAdded('read');
  const pendingWrites = notYetAdded('write');
  const addAll = (cls: 'read' | 'write', tools: DiscoveredTool[]) =>
    void Promise.all(
      tools.map((tool) =>
        createEntry.mutateAsync({
          connectionId,
          toolName: tool.name,
          toolClass: cls,
          enabled: true,
        }),
      ),
    );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{connection.name}</h1>
          <p className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <span>{new URL(connection.url).host}</span>
            {connection.enabled ? (
              <Badge variant="secondary">Enabled</Badge>
            ) : (
              <Badge variant="outline">Disabled</Badge>
            )}
            {connection.status.connected ? (
              <Badge variant="secondary">Connected ({connection.status.liveToolCount})</Badge>
            ) : (
              <Badge variant="outline">Disconnected</Badge>
            )}
            {connection.maxConcurrentCalls !== null && (
              <Badge variant="outline">Max {connection.maxConcurrentCalls} concurrent</Badge>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/mcp/connections/$connectionId/edit" params={{ connectionId }}>
              <Pencil className="size-4" />
              Edit
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/mcp/connections">Back</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Connection</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <dt className="text-sm font-medium">Bearer token</dt>
              <dd className="text-sm text-[var(--color-muted-foreground)]">
                {connection.bearerTokenSet ? 'Configured' : 'Not configured'}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Allowlist</CardTitle>
          <Button
            size="sm"
            variant="outline"
            disabled={deleteAll.isPending || connection.allowlist.length === 0}
            onClick={() => deleteAll.mutate()}
          >
            Delete all
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tool</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {connection.allowlist.length ? (
                  connection.allowlist.map((entry) => <AllowlistRow key={entry.id} entry={entry} />)
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="h-20 text-center">
                      No allowlisted tools yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Discover tools</CardTitle>
          <Button
            size="sm"
            onClick={() => discoverTools.mutate()}
            disabled={discoverTools.isPending}
          >
            {discoverTools.isPending ? 'Fetching…' : 'Fetch tools'}
          </Button>
        </CardHeader>
        <CardContent>
          {discoverTools.data?.ok === false && (
            <p className="text-sm text-[var(--color-destructive)]">{discoverTools.data.reason}</p>
          )}

          {discoverTools.data?.ok === true &&
            (discoverTools.data.tools.length ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={createEntry.isPending || pendingReads.length === 0}
                    onClick={() => addAll('read', pendingReads)}
                  >
                    Add all reads ({pendingReads.length})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={createEntry.isPending || pendingWrites.length === 0}
                    onClick={() => addAll('write', pendingWrites)}
                  >
                    Add all writes ({pendingWrites.length})
                  </Button>
                </div>
                <div className="rounded-md border">
                  <Table className="table-fixed">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[30%]">Tool</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-[88px]">Class</TableHead>
                        <TableHead className="w-[96px] text-right" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {discoverTools.data.tools.map((tool) => (
                        <DiscoveredToolRow
                          key={tool.name}
                          connectionId={connectionId}
                          tool={tool}
                          isAllowlisted={allowlistedNames.has(tool.name)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[var(--color-muted-foreground)]">
                The remote server reported no tools.
              </p>
            ))}

          {!discoverTools.data && (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Fetch the remote server's live tool list to allowlist new tools.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
